import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { userService } from '../services/userService';
import { recordViewEvent } from '../services/viewTelemetryService';
import { useTenantContext } from './useTenantContext';
import {
    resolveViewMode,
    setViewMode as persistViewMode,
    VIEW_MODES,
} from '../config/viewMode';

const ViewModeContext = createContext(null);

export const ViewModeProvider = ({ children }) => {
    const location = useLocation();
    const user = useSelector((state) => state.user?.userData);
    const tenantContext = useTenantContext();
    const [viewMode, setViewModeState] = useState(() => resolveViewMode({ user, tenantContext }));

    useEffect(() => {
        setViewModeState(resolveViewMode({
            search: location.search,
            user,
            tenantContext,
        }));
    }, [location.search, tenantContext, user, user?.preferences?.viewMode]);

    useEffect(() => {
        recordViewEvent({ eventType: 'page_view', viewMode, path: location.pathname });
    }, [location.pathname, viewMode]);

    useEffect(() => {
        const reportError = (errorName) => recordViewEvent({
            eventType: 'error',
            viewMode,
            path: location.pathname,
            errorName,
        });
        const onError = (event) => reportError(event.error?.name || 'window_error');
        const onRejection = (event) => reportError(event.reason?.name || 'unhandled_rejection');
        window.addEventListener('error', onError);
        window.addEventListener('unhandledrejection', onRejection);
        return () => {
            window.removeEventListener('error', onError);
            window.removeEventListener('unhandledrejection', onRejection);
        };
    }, [location.pathname, viewMode]);

    const setViewMode = useCallback((nextMode) => {
        const next = persistViewMode(nextMode);
        if (next !== viewMode) {
            recordViewEvent({
                eventType: 'switch',
                viewMode: next,
                direction: `${viewMode}->${next}`,
            });
        }
        setViewModeState(next);

        // The browser preference is enough to switch immediately. Persist the
        // same choice on the user record when the endpoint is available; a
        // network failure must never trap someone in either shell.
        if (user?.id) {
            userService.updateViewModePreference(user.id, next).catch(() => {});
        }
        return next;
    }, [user?.id, viewMode]);

    const value = useMemo(() => ({
        viewMode,
        isModern: viewMode === VIEW_MODES.MODERN,
        isLegacy: viewMode === VIEW_MODES.LEGACY,
        setViewMode,
    }), [setViewMode, viewMode]);

    return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>;
};

export const useViewMode = () => {
    const context = useContext(ViewModeContext);
    // Shared legacy components and their isolated tests may render outside the
    // authenticated app provider. Their safe behavior is the frozen legacy view.
    return context || {
        viewMode: VIEW_MODES.LEGACY,
        isModern: false,
        isLegacy: true,
        setViewMode: persistViewMode,
    };
};
